import { supabase } from "./supabase";

// WARNING: For production, store this in an environment variable!
const OPENAI_API_KEY = "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";

// Minimal JSON sanitization (only remove problematic control chars, no heavy quote escaping)
function sanitizeJsonString(str: string) {
  return (
    str
      // Remove control characters
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
      // Remove zero-width/BOM chars
      .replace(/[\u200B-\u200D\u2060\uFEFF]/g, "")
      // Remove LTR/RTL markers
      .replace(/[\u200E\u200F]/g, "")
      .trim()
  );
}

// If you need to parse HTML for other features, keep or remove as needed
function cleanHtml(html: string): { content: string; structuredData: any } {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    let structuredData: any = {};

    // Example: parse <script type="application/ld+json"> if needed
    const jsonLdScripts = doc.querySelectorAll(
      'script[type="application/ld+json"]'
    );
    jsonLdScripts.forEach((script) => {
      try {
        const data = JSON.parse(script.textContent || "");
        if (
          data["@type"] === "Recipe" ||
          (Array.isArray(data["@graph"]) &&
            data["@graph"].some((item) => item["@type"] === "Recipe"))
        ) {
          structuredData = data;
        }
      } catch (e) {
        console.warn("Failed to parse JSON-LD:", e);
      }
    });

    return { content: "", structuredData };
  } catch (error) {
    console.error("Error cleaning HTML:", error);
    return { content: "", structuredData: {} };
  }
}

// ===================== Favorite Recipes =====================
export async function getFavoriteRecipes() {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  const { data, error } = await supabase
    .from("favorite_recipes")
    .select("*")
    .eq("user_id", user.id);

  if (error) throw error;
  return data;
}

export async function addFavoriteRecipe(recipe: any) {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  const { error } = await supabase.from("favorite_recipes").insert({
    user_id: user.id,
    recipe,
  });

  if (error) throw error;
}

export async function removeFavoriteRecipe(recipeId: string) {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  const { error } = await supabase
    .from("favorite_recipes")
    .delete()
    .eq("id", recipeId);

  if (error) throw error;
}

// ===================== Meal Plan Generation =====================
export async function generateMealPlan(preferences: any) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  // Create ingredient preference strings
  const includedIngredients =
    preferences.ingredients?.include?.join(", ") || "none";
  const excludedIngredients =
    preferences.ingredients?.exclude?.join(", ") || "none";

  try {
    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${OPENAI_API_KEY}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "gpt-4o-mini", // or 'gpt-4'
        max_tokens: 16000,
        temperature: 0.7,
        messages: [
          {
            role: "system",
            content: `
You are a meal planning assistant that creates personalized meal plans based on user preferences.
Generate all content in Hebrew.
Return ONLY valid JSON with no extra text.
Start with '{' and end with '}', must be valid JSON.
Generate EXACTLY the number of days requested—no more, no fewer.

Each recipe instruction should be very detailed and include:
- Exact measurements and quantities
- Specific cooking temperatures when relevant
- Precise cooking times
- Visual cues for doneness
- Important technique details
- Tips within the steps when crucial
- Safety precautions when needed

Each day must have this structure:
{
  "dayNumber": <number>,
  "meals": {
    "breakfast": {
      "name": "...",
      "description": "...",
      "cookingTime": 0,
      "servings": ${preferences.servings},
      "recipe": {
        "prepTime": 0,
        "cookTime": 0,
        "ingredients": [],
        "instructions": [],
        "tips": [],
        "nutrition": {
          "calories": 0,
          "protein": 0,
          "carbs": 0,
          "fat": 0,
          "fiber": 0
        }
      }
    },
    "lunch": {
      "name": "...",
      "description": "...",
      "cookingTime": 0,
      "servings": ${preferences.servings},
      "recipe": {
        "prepTime": 0,
        "cookTime": 0,
        "ingredients": [],
        "instructions": [],
        "tips": [],
        "nutrition": {
          "calories": 0,
          "protein": 0,
          "carbs": 0,
          "fat": 0,
          "fiber": 0
        }
      }
    },
    "dinner": {
      "name": "...",
      "description": "...",
      "cookingTime": 0,
      "servings": ${preferences.servings},
      "recipe": {
        "prepTime": 0,
        "cookTime": 0,
        "ingredients": [],
        "instructions": [],
        "tips": [],
        "nutrition": {
          "calories": 0,
          "protein": 0,
          "carbs": 0,
          "fat": 0,
          "fiber": 0
        }
      }
    }
  }
}
          `,
          },
          {
            role: "user",
            content: `
Create a detailed meal plan for EXACTLY ${preferences.duration} days. 
For each day, provide breakfast, lunch, and dinner in the structure above.

can include these ingredients, this are user likes: ${includedIngredients}
MUST NOT include these ingredients: ${excludedIngredients}
Each meal:
- Takes no more than ${preferences.cookingTime} minutes total
- Has ingredients for ${preferences.servings} servings
- Fully in Hebrew
- Must be unique if possible (avoid repeating the same recipe)
- Respect dietary restrictions
- Provide cooking tips
- No excluded ingredients

Preferences:
${JSON.stringify(preferences, null, 2)}

Output ONLY valid JSON with an array \`days\` of length ${preferences.duration}.
No extra text, no markdown. 
`
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status} - ${await response.text()}`);
    }

    const data = await response.json();
    if (!data.choices?.[0]?.message?.content) {
      throw new Error("Invalid API response format (missing 'content')");
    }

    // Parse and validate the response
    let content = data.choices[0].message.content.trim();
    
    // Remove any potential leading/trailing characters that aren't part of the JSON
    content = content.replace(/^[^{]*/, '').replace(/[^}]*$/, '');
    
    try {
      const mealPlan = JSON.parse(content);
      
      // Validate the plan structure
      if (!mealPlan.days || !Array.isArray(mealPlan.days)) {
        throw new Error("Invalid meal plan format: missing days array");
      }
      
      console.log("Parsed meal plan days:", mealPlan.days.length);

      // Save to Supabase
      const { data: savedPlan, error: saveError } = await supabase
        .from("meal_plans")
        .insert({
          user_id: user.id,
          plan_data: mealPlan,
          preferences: preferences,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (saveError) {
        console.error("Failed to save meal plan:", saveError);
        throw saveError;
      }

      return savedPlan;
    } catch (error) {
      console.error("JSON Parse Error:", error);
      console.log("Raw content:", content);
      throw new Error(`Failed to parse meal plan JSON: ${error instanceof Error ? error.message : String(error)}`);
    }
  } catch (error) {
    console.error("Meal plan generation error:", error);
    throw error;
  }
}

// ===================== Meal Plan DB Operations =====================
export async function removeMealPlan(planId: string) {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  const { error } = await supabase.from("meal_plans").delete().eq("id", planId);
  if (error) throw error;
}

export async function getUserMealPlans() {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  const { data, error } = await supabase
    .from("meal_plans")
    .select("*")
    .eq("user_id", user.id)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data;
}

export async function updateMealInPlan(
  dayNumber: number,
  mealType: "breakfast" | "lunch" | "dinner",
  newMeal: any,
  planId: string
) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  try {
    // Get current plan
    const { data: plan, error: fetchError } = await supabase
      .from("meal_plans")
      .select("*")
      .eq("id", planId)
      .single();

    if (fetchError) throw fetchError;
    if (!plan) throw new Error("Meal plan not found");

    // Create a deep copy of the plan data
    const updatedPlanData = JSON.parse(JSON.stringify(plan.plan_data));
    
    // Find the day to update
    const dayIndex = updatedPlanData.days.findIndex(
      (d: any) => d.dayNumber === dayNumber
    );
    
    if (dayIndex === -1) {
      throw new Error(`Day ${dayNumber} not found in plan`);
    }

    // Update the specific meal
    updatedPlanData.days[dayIndex].meals[mealType] = newMeal;

    // Save the updated plan
    const { data: updatedPlan, error: updateError } = await supabase
      .from("meal_plans")
      .update({ plan_data: updatedPlanData })
      .eq("id", planId)
      .select()
      .single();

    if (updateError) throw updateError;
    
    console.log("Plan updated successfully:", updatedPlan);
    return updatedPlan.plan_data;
  } catch (error) {
    console.error("Failed to update meal:", error);
    throw error;
  }
}

// If you need recipe import from URL...
export async function importRecipeFromUrl(url: string) {
  // Placeholder for your scraping/HTML parsing logic
  return null;
}
